import { X } from "lucide-react";
import { useEffect, useRef } from "react";

interface DefinitionPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  definition: string;
  items: Array<{ label?: string; text: string }>;
  proTip?: string;
  anchorRef: React.RefObject<HTMLElement>;
}

export function DefinitionPopover({
  isOpen,
  onClose,
  title,
  definition,
  items,
  proTip,
  anchorRef
}: DefinitionPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  // Position the popover relative to the anchor element
  useEffect(() => {
    if (!isOpen || !popoverRef.current || !anchorRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = anchorRect.bottom + 8; // 8px gap below anchor
    let left = anchorRect.left;

    // Smart positioning: flip above if too close to bottom
    if (top + popoverRect.height > viewportHeight - 20) {
      top = anchorRect.top - popoverRect.height - 8;
    }

    // Adjust horizontal position if too close to right edge
    if (left + popoverRect.width > viewportWidth - 20) {
      left = viewportWidth - popoverRect.width - 20;
    }

    // Ensure not too far left
    if (left < 20) {
      left = 20;
    }

    popoverRef.current.style.top = `${top}px`;
    popoverRef.current.style.left = `${left}px`;
  }, [isOpen, anchorRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 rounded-lg shadow-lg"
      style={{
        backgroundColor: 'var(--background)',
        border: '1px solid var(--border)',
        maxWidth: '420px',
        width: '420px',
        borderRadius: 'var(--radius-lg)'
      }}
    >
      {/* Header */}
      <div 
        className="flex items-start justify-between p-4 pb-3 border-b"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-2">
          <div 
            className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'var(--spectral-violet-10)' }}
          >
            <svg 
              width="12" 
              height="12" 
              viewBox="0 0 12 12" 
              fill="none"
              style={{ color: 'var(--spectral-violet)' }}
            >
              <path 
                d="M6 1L7.5 4.5L11 6L7.5 7.5L6 11L4.5 7.5L1 6L4.5 4.5L6 1Z" 
                fill="currentColor"
              />
            </svg>
          </div>
          <h4 
            style={{ 
              fontSize: 'var(--text-base)', 
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--foreground)'
            }}
          >
            {title}
          </h4>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 hover:opacity-70 transition-opacity"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Definition */}
        <p 
          style={{ 
            fontSize: 'var(--text-sm)', 
            color: 'var(--foreground)',
            lineHeight: '1.5'
          }}
        >
          {definition}
        </p>

        {/* Items List */}
        {items.length > 0 && (
          <div className="space-y-3">
            {items.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <span 
                  style={{ 
                    color: 'var(--muted-foreground)',
                    fontSize: 'var(--text-sm)',
                    flexShrink: 0
                  }}
                >
                  •
                </span>
                <div>
                  {item.label && (
                    <span 
                      style={{ 
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--foreground)'
                      }}
                    >
                      {item.label}
                      <br />
                    </span>
                  )}
                  <span 
                    style={{ 
                      fontSize: 'var(--text-sm)', 
                      color: 'var(--foreground)',
                      lineHeight: '1.5'
                    }}
                  >
                    {item.text}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pro Tip */}
        {proTip && (
          <div 
            className="rounded p-3"
            style={{ 
              backgroundColor: 'var(--spectral-violet-10)',
              borderLeft: '3px solid var(--spectral-violet)'
            }}
          >
            <div className="flex items-start gap-2">
              <div 
                className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ backgroundColor: 'var(--spectral-violet)' }}
              >
                <svg 
                  width="10" 
                  height="10" 
                  viewBox="0 0 12 12" 
                  fill="none"
                  style={{ color: 'var(--background)' }}
                >
                  <path 
                    d="M6 1L7.5 4.5L11 6L7.5 7.5L6 11L4.5 7.5L1 6L4.5 4.5L6 1Z" 
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div>
                <div 
                  style={{ 
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--spectral-violet)',
                    marginBottom: '4px'
                  }}
                >
                  Pro tip
                </div>
                <p 
                  style={{ 
                    fontSize: 'var(--text-sm)', 
                    color: 'var(--foreground)',
                    lineHeight: '1.5'
                  }}
                >
                  {proTip}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
